// src/components/TokenRedemptionPanel.jsx - Further improved version
import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';

const TokenRedemptionPanel = ({ 
  contract, 
  fundTokenBalance, 
  isLoading, 
  setError,
  setSuccess,
  onRedeemToken // Callback for App.js integration
}) => {
  const [tokens, setTokens] = useState([]);
  const [selectedToken, setSelectedToken] = useState(null);
  const [redeemAmount, setRedeemAmount] = useState('');
  const [loadingTokens, setLoadingTokens] = useState(true);

  // Load token information when component mounts
  useEffect(() => {
    const loadTokens = async () => {
      try {
        setLoadingTokens(true);
        
        if (contract) {
          // Get all token information from the contract
          const tokenInfo = await contract.getAllTokenInfo();
          const addresses = tokenInfo[0];
          const symbols = tokenInfo[1];
          const indices = tokenInfo[2];
          
          // Get fund's token balances
          const tokenDetails = await Promise.all(
            addresses.map(async (address, i) => {
              const tokenContract = new ethers.Contract(
                address,
                ["function balanceOf(address) view returns (uint256)", "function decimals() view returns (uint8)"],
                contract.provider
              );
              
              const balance = await tokenContract.balanceOf(contract.address);
              let decimals = 18;
              
              try {
                decimals = await tokenContract.decimals();
              } catch (error) {
                console.warn(`Could not get decimals for token at ${address}, using default 18`);
              }
              
              return {
                address,
                symbol: symbols[i] || `Token ${i}`,
                index: indices[i].toNumber(),
                balance: ethers.utils.formatUnits(balance, decimals),
                decimals,
                rawBalance: balance
              };
            })
          );
          
          setTokens(tokenDetails);
          
          // Set first token as default if available
          if (tokenDetails.length > 0) {
            setSelectedToken(tokenDetails[0]);
          }
        }
      } catch (error) {
        console.error('Error loading tokens:', error);
        setError('Failed to load token information');
      } finally {
        setLoadingTokens(false);
      }
    };
    
    loadTokens();
  }, [contract, setError]);

  // Enhanced redeem handler with better validation
  const handleRedeem = async () => {
    if (!selectedToken || !redeemAmount || parseFloat(redeemAmount) <= 0) {
      setError('Please select a token and enter a valid amount');
      return;
    }
    
    const maxBalance = parseFloat(fundTokenBalance);
    const requestedAmount = parseFloat(redeemAmount);
    
    // Improved validation with clear error message
    if (requestedAmount > maxBalance) {
      setError(`Insufficient fund token balance. Maximum available: ${maxBalance.toFixed(6)} MCIF`);
      return;
    }
    
    try {
      // Call the parent component's handler function with the VERIFIED amount
      const safeAmount = ethers.utils.parseEther(redeemAmount.toString())
        .lt(ethers.utils.parseEther(fundTokenBalance))
        ? redeemAmount
        : ethers.utils.formatEther(
            ethers.utils.parseEther(fundTokenBalance)
              .mul(995)
              .div(1000)
          ); // 99.5% of max
          
      onRedeemToken(selectedToken.index, safeAmount, selectedToken.symbol);
      
      // Clear the input field
      setRedeemAmount('');
    } catch (error) {
      console.error('Error preparing token redemption:', error);
      setError(error.message || 'Failed to prepare token redemption');
    }
  };

  // Improved input validation
  const handleInputChange = (e) => {
    const value = e.target.value;
    
    // Allow empty input for user convenience while typing
    if (value === '') {
      setRedeemAmount('');
      return;
    }
    
    // Only allow numbers and a single decimal point
    if (/^[0-9]*\.?[0-9]*$/.test(value)) {
      // Prevent leading zeros (like 01.5)
      if (value.length > 1 && value[0] === '0' && value[1] !== '.') {
        setRedeemAmount(value.substring(1));
        return;
      }
      
      // Prevent multiple decimal points
      const decimalCount = (value.match(/\./g) || []).length;
      if (decimalCount <= 1) {
        // Limit to 18 decimal places
        const parts = value.split('.');
        if (parts.length > 1 && parts[1].length > 18) {
          return;
        }
        
        // Set the value
        setRedeemAmount(value);
      }
    }
  };

  // IMPROVED: Use BigNumber math for safer calculations
  const calculateSafeMax = () => {
    try {
      // Convert the string to a BigNumber
      const balanceBN = ethers.utils.parseEther(fundTokenBalance || '0');
      
      if (balanceBN.isZero()) {
        return '0';
      }
      
      // Use 99.5% of the balance to account for gas costs and rounding
      // 99.5% = 995/1000
      const safeMax = balanceBN.mul(995).div(1000);
      
      // Convert back to a string with proper formatting
      return ethers.utils.formatEther(safeMax);
    } catch (error) {
      console.error('Error calculating safe maximum:', error);
      return '0';
    }
  };

  if (loadingTokens) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-md mb-6 animate-pulse">
        <h3 className="text-lg font-medium mb-4">Loading Tokens...</h3>
        <div className="h-8 bg-gray-200 rounded w-full mb-4"></div>
        <div className="h-10 bg-gray-200 rounded w-full mb-4"></div>
        <div className="h-10 bg-gray-200 rounded w-full"></div>
      </div>
    );
  }

  // Calculate the safe max value once
  const safeMaxAmount = calculateSafeMax();
  // Check if user amount exceeds balance
  const exceedsBalance = parseFloat(redeemAmount) > parseFloat(fundTokenBalance);

  return (
    <div className="bg-white rounded-lg p-6 shadow-md mb-6">
      <h3 className="text-lg font-medium mb-4">Redeem Tokens Directly</h3>
      <p className="text-sm text-gray-600 mb-4">
        Redeem your fund tokens for specific underlying tokens. 
        This will withdraw the token directly to your wallet without converting to ETH first.
      </p>
      
      {tokens.length === 0 ? (
        <div className="text-center p-4 bg-gray-100 rounded-lg">
          <p className="text-gray-600">No tokens available in the fund</p>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Token</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={selectedToken ? selectedToken.index : ''}
              onChange={(e) => {
                const index = parseInt(e.target.value);
                const token = tokens.find(t => t.index === index);
                setSelectedToken(token || null);
              }}
              disabled={isLoading}
            >
              {tokens.map(token => (
                <option key={token.index} value={token.index}>
                  {token.symbol} - Balance: {parseFloat(token.balance).toFixed(6)}
                </option>
              ))}
            </select>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fund Tokens to Redeem (MCIF)
            </label>
            <input
              type="text"
              className={`w-full px-3 py-2 border ${
                exceedsBalance ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
              } rounded-md shadow-sm focus:outline-none focus:border-transparent`}
              placeholder="Enter amount"
              value={redeemAmount}
              onChange={handleInputChange}
              disabled={isLoading}
            />
            
            <div className="mt-1 flex justify-between text-xs text-gray-500">
              <span>Available: {parseFloat(fundTokenBalance).toFixed(6)} MCIF</span>
              
              <button 
                type="button" 
                className="text-blue-500 hover:text-blue-700"
                onClick={() => setRedeemAmount(safeMaxAmount)}
                disabled={isLoading}
              >
                Max
              </button>
            </div>
            
            {exceedsBalance && (
              <p className="mt-1 text-xs text-red-500">
                Amount exceeds your available balance
              </p>
            )}
          </div>
          
          <button
            className={`w-full py-2 px-4 rounded-lg font-medium text-white 
              ${isLoading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : exceedsBalance
                  ? 'bg-red-400 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600'
              } 
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition`}
            onClick={handleRedeem}
            disabled={
              isLoading || 
              !redeemAmount || 
              parseFloat(redeemAmount) <= 0 || 
              !selectedToken ||
              exceedsBalance
            }
          >
            {isLoading 
              ? 'Processing...' 
              : exceedsBalance
                ? 'Amount Exceeds Balance'
                : `Redeem for ${selectedToken ? selectedToken.symbol : 'Token'}`
            }
          </button>
          
          {selectedToken && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm">
              <p className="font-medium">Transaction Preview:</p>
              <p className="text-gray-600">
                You will redeem {redeemAmount || '0'} MCIF tokens for approximately{' '}
                {redeemAmount && selectedToken.rawBalance && selectedToken.rawBalance.gt(0) ? 
                  parseFloat((parseFloat(redeemAmount) * parseFloat(selectedToken.balance) / parseFloat(fundTokenBalance)).toFixed(6)) : '0'}{' '}
                {selectedToken.symbol}
              </p>
              {exceedsBalance && (
                <p className="text-red-500 text-xs mt-1">
                  Warning: Amount exceeds your available balance of {parseFloat(fundTokenBalance).toFixed(6)} MCIF tokens.
                </p>
              )}
              <p className="text-gray-500 text-xs mt-1">
                Note: Actual amounts may vary slightly due to price fluctuations and fees.
                For best results, use slightly less than your maximum balance.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TokenRedemptionPanel;